import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Departments() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name:'', head_user_id:'', description:'' });
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/departments'),
      get('/users')
    ]).then(([d, u]) => {
      setDepartments(d.data||[]);
      setUsers(u.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadChecklist = (deptId) => {
    get('/departments/'+deptId+'/checklist').then(d => setChecklist(d.data||[]));
  };

  const selectDept = (dept) => {
    setSelected(dept);
    loadChecklist(dept.id);
  };

  const saveDept = async () => {
    setSaving(true);
    try {
      if (editDept) {
        await put('/departments/'+editDept.id, deptForm);
      } else {
        await post('/departments', deptForm);
      }
      setShowDeptForm(false);
      setEditDept(null);
      setDeptForm({ name:'', head_user_id:'', description:'' });
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const addChecklistItem = async () => {
    if (!newItem.trim() || !selected) return;
    try {
      await post('/departments/'+selected.id+'/checklist', { item: newItem, sort_order: checklist.length });
      setNewItem('');
      loadChecklist(selected.id);
    } catch(err) { alert(err.message); }
  };

  const removeChecklistItem = async (itemId) => {
    if (!window.confirm('Remove this checklist item?')) return;
    try {
      await del('/departments/'+selected.id+'/checklist/'+itemId);
      loadChecklist(selected.id);
    } catch(err) { alert(err.message); }
  };

  const deactivateDept = async (dept) => {
    if (!window.confirm('Delete department "'+dept.name+'"? This cannot be undone.')) return;
    try {
      await del('/departments/'+dept.id);
      setSelected(null);
      load();
    } catch(err) { alert(err.message); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Departments</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Manage departments, heads and closing checklists</p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => { setEditDept(null); setDeptForm({ name:'', head_user_id:'', description:'' }); setShowDeptForm(true); }}>
            + Add Department
          </button>
        )}
      </div>

      <div style={{display:'grid', gridTemplateColumns:'280px 1fr', gap:20}}>
        {/* Department list */}
        <div style={{background:'#fff', border:'1px solid var(--border-light)', borderRadius:10, overflow:'hidden'}}>
          <div style={{padding:'10px 16px', background:'#F2EFE7', fontSize:10, fontWeight:600, color:'#556B2F', textTransform:'uppercase', letterSpacing:'.08em'}}>
            Departments ({departments.length})
          </div>
          {loading ? <div className="spinner" style={{margin:20}}></div> : departments.length === 0 ? (
            <div style={{padding:20, textAlign:'center', color:'#8A8A8A', fontSize:13}}>No departments yet</div>
          ) : departments.map(d => (
            <div key={d.id} onClick={() => selectDept(d)}
              style={{padding:'12px 16px', cursor:'pointer', borderBottom:'1px solid #F2EFE7',
                background: selected?.id===d.id ? '#F2EFE7' : '#fff'}}>
              <div style={{fontSize:13, fontWeight:600, color:'#3B2A21'}}>{d.name}</div>
              <div style={{fontSize:11, color:'#8A8A8A', marginTop:2}}>
                {d.head_name ? 'Head: '+d.head_name : 'No head assigned'}
              </div>
              <div style={{fontSize:10, color:'#556B2F', marginTop:2}}>
                {d.checklist_count} checklist items
              </div>
            </div>
          ))}
        </div>

        {/* Department detail */}
        <div>
          {!selected ? (
            <div className="empty-state">
              <h3>Select a department</h3>
              <p>Click a department to view and configure its checklist</p>
            </div>
          ) : (
            <div>
              {/* Dept header */}
              <div style={{background:'#fff', border:'1px solid var(--border-light)', borderRadius:10, padding:20, marginBottom:16}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                  <div>
                    <h3 style={{fontFamily:'Cormorant Garamond,serif', fontSize:'1.3rem', color:'#3B2A21', margin:0}}>{selected.name}</h3>
                    <div style={{fontSize:12, color:'#8A8A8A', marginTop:4}}>
                      Department Head: <strong>{selected.head_name || 'Not assigned'}</strong>
                    </div>
                    {selected.description && <div style={{fontSize:12, color:'#555', marginTop:4}}>{selected.description}</div>}
                  </div>
                  {isSuperAdmin && (
                    <div style={{display:'flex', gap:8}}>
                      <button className="btn btn-outline btn-sm" onClick={() => {
                        setEditDept(selected);
                        setDeptForm({ name:selected.name, head_user_id:selected.head_user_id||'', description:selected.description||'' });
                        setShowDeptForm(true);
                      }}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                        onClick={() => deactivateDept(selected)}>Delete</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Checklist */}
              <div style={{background:'#fff', border:'1px solid var(--border-light)', borderRadius:10, overflow:'hidden'}}>
                <div style={{padding:'12px 16px', background:'#3B2A21', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <span style={{fontFamily:'Cormorant Garamond,serif', fontSize:'1rem', color:'#A8893D', fontWeight:600}}>
                    ✅ Closing Checklist ({checklist.length} items)
                  </span>
                </div>
                <div style={{padding:16}}>
                  {checklist.length === 0 ? (
                    <div style={{color:'#8A8A8A', fontSize:13, marginBottom:16}}>No checklist items yet. Add items below.</div>
                  ) : checklist.map((item, i) => (
                    <div key={item.id} style={{display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #F2EFE7'}}>
                      <span style={{fontSize:16, color:'#556B2F'}}>☐</span>
                      <div style={{flex:1}}>
                        <span style={{fontSize:13, color:'#3B2A21'}}>{item.item}</span>
                        {item.is_required && <span style={{fontSize:9, marginLeft:8, background:'#FDEAEA', color:'#8F2E24', padding:'1px 6px', borderRadius:10}}>Required</span>}
                      </div>
                      {isSuperAdmin && (
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10, color:'#8F2E24'}}
                          onClick={() => removeChecklistItem(item.id)}>✕</button>
                      )}
                    </div>
                  ))}

                  {isSuperAdmin && (
                    <div style={{display:'flex', gap:8, marginTop:16}}>
                      <input className="form-control" placeholder="Add checklist item..."
                        value={newItem} onChange={e => setNewItem(e.target.value)}
                        onKeyDown={e => e.key==='Enter' && addChecklistItem()}
                        style={{flex:1}} />
                      <button className="btn btn-primary btn-sm" onClick={addChecklistItem}>Add</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Department form modal */}
      {showDeptForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-hdr">
              <span className="modal-title">{editDept ? 'Edit' : 'New'} Department</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDeptForm(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Department Name *</label>
                <input className="form-control" value={deptForm.name}
                  onChange={e => setDeptForm({...deptForm, name:e.target.value})}
                  placeholder="e.g. Production" />
              </div>
              <div className="form-group">
                <label className="form-label">Department Head</label>
                <select className="form-control" value={deptForm.head_user_id}
                  onChange={e => setDeptForm({...deptForm, head_user_id:e.target.value})}>
                  <option value="">Select department head...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={deptForm.description}
                  onChange={e => setDeptForm({...deptForm, description:e.target.value})}
                  placeholder="Brief description of this department" />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={() => setShowDeptForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDept} disabled={saving||!deptForm.name}>
                {saving ? 'Saving...' : editDept ? 'Save Changes' : 'Create Department'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
